/**
 * Create organization dialog component.
 *
 * Reusable dialog for creating new organizations with
 * auto-generated slug from name.
 */

import {
  Button,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from "@repo/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Building2, UserStar } from "lucide-react";
import { useState } from "react";
import { captureEvent } from "../../lib/analytics-loader";
import { organizationKeys } from "../../lib/queries/organization";
import { trpcClient } from "../../lib/trpc";

interface CreateOrganizationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parentOrganizationId?: string;
  onSuccess?: () => void;
  title?: string;
  description?: string;
}

export function CreateOrganizationDialog({
  open,
  onOpenChange,
  parentOrganizationId,
  onSuccess,
  title = "Add Account",
  description = "Create a new account to get started.",
}: CreateOrganizationDialogProps) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [email, setEmail] = useState("");
  const [organizationType, setOrganizationType] = useState<
    "creator" | "agency"
  >("creator");
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Auto-generate slug from name if slug hasn't been manually edited
  const handleNameChange = (value: string) => {
    setName(value);
    if (!slugTouched) {
      setSlug(
        value
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-|-$/g, ""),
      );
    }
  };

  const [createdOrg, setCreatedOrg] = useState<{
    id: string;
    slug: string;
    name: string;
  } | null>(null);

  const createMutation = useMutation({
    mutationFn: (data: {
      name: string;
      slug: string;
      parentOrganizationId?: string;
      metadata?: Record<string, unknown>;
    }) => trpcClient.organization.create.mutate(data),
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });

      void captureEvent("organization_created", {
        name: data.name,
        slug: data.slug,
      });

      // For Creator accounts, auto-create a profile with the account name
      if (organizationType === "creator") {
        try {
          await trpcClient.organization.profiles.create.mutate({
            organizationId: data.id,
            name: data.name,
            slug: data.slug,
            email: email.trim() || undefined,
          });
        } catch (err) {
          // Profile creation failure is non-blocking — org was created successfully
          console.error(
            "[CreateOrganizationDialog] Failed to auto-create creator profile:",
            err,
          );
        }
      }

      if (onSuccess) {
        onSuccess();
      }
      setCreatedOrg(data);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim() && slug.trim()) {
      const metadata = { organizationType };
      createMutation.mutate({
        name: name.trim(),
        slug: slug.trim(),
        parentOrganizationId,
        metadata,
      });
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      // Reset form when closing
      setName("");
      setSlug("");
      setSlugTouched(false);
      setEmail("");
      setCreatedOrg(null);
      setOrganizationType("creator");
    }
    onOpenChange(newOpen);
  };

  const handleGoToOrg = () => {
    if (createdOrg) {
      handleOpenChange(false);
      navigate({
        to: "/o/$orgSlug",
        params: { orgSlug: createdOrg.slug },
      });
    }
  };

  return (
    <ResponsiveDialog open={open} onOpenChange={handleOpenChange}>
      <ResponsiveDialogContent className="sm:max-w-lg">
        {createdOrg ? (
          <div className="flex flex-col">
            <ResponsiveDialogHeader>
              <ResponsiveDialogTitle>
                Account Created Successfully
              </ResponsiveDialogTitle>
              <ResponsiveDialogDescription>
                You have successfully created <strong>{createdOrg.name}</strong>
                .
              </ResponsiveDialogDescription>
            </ResponsiveDialogHeader>

            <div className="py-8">
              <div className="flex flex-col gap-4 items-center justify-center text-center">
                <div className="rounded-full bg-green-100 p-3 dark:bg-green-900/20">
                  <svg
                    className="h-6 w-6 text-green-600 dark:text-green-500"
                    fill="none"
                    height="24"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    width="24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <p className="text-muted-foreground">
                  What would you like to do next?
                </p>
              </div>
            </div>

            <ResponsiveDialogFooter className="gap-2 sm:justify-between">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                className="w-full sm:w-auto"
              >
                Continue Here
              </Button>
              <Button
                type="button"
                onClick={handleGoToOrg}
                className="w-full sm:w-auto"
              >
                Go to {createdOrg.name}
              </Button>
            </ResponsiveDialogFooter>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <ResponsiveDialogHeader>
              <ResponsiveDialogTitle>{title}</ResponsiveDialogTitle>
              <ResponsiveDialogDescription>
                {description}
              </ResponsiveDialogDescription>
            </ResponsiveDialogHeader>

            <div className="space-y-4 py-4 px-6">
              <div className="space-y-2">
                <Label htmlFor="name">Account Name</Label>
                <Input
                  id="name"
                  placeholder="My Account"
                  value={name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  disabled={createMutation.isPending}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="slug">URL</Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">/o/</span>
                  <Input
                    id="slug"
                    placeholder="my-account"
                    value={slug}
                    onChange={(e) => {
                      setSlugTouched(true);
                      setSlug(
                        e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""),
                      );
                    }}
                    disabled={createMutation.isPending}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  This will be used in URLs: /o/{slug || "my-account"}
                </p>
              </div>

              <div className="space-y-2">
                <Label>Account Type</Label>
                <RadioGroup
                  value={organizationType}
                  onValueChange={(value: "creator" | "agency") =>
                    setOrganizationType(value)
                  }
                  disabled={createMutation.isPending}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="creator" id="dialog-type-creator" />
                    <Label
                      htmlFor="dialog-type-creator"
                      className="flex items-center gap-2 cursor-pointer font-normal"
                    >
                      <UserStar className="h-4 w-4" />
                      Creator
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="agency" id="dialog-type-agency" />
                    <Label
                      htmlFor="dialog-type-agency"
                      className="flex items-center gap-2 cursor-pointer font-normal"
                    >
                      <Building2 className="h-4 w-4" />
                      Agency
                    </Label>
                  </div>
                </RadioGroup>
                <p className="text-xs text-muted-foreground">
                  Choose <strong>Creator</strong> for individual content
                  creators or influencers. Choose <strong>Agency</strong> to
                  manage multiple creators.
                </p>
              </div>

              {organizationType === "creator" && (
                <div className="space-y-2">
                  <Label htmlFor="org-email">Email Address (optional)</Label>
                  <Input
                    id="org-email"
                    type="email"
                    placeholder="creator@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={createMutation.isPending}
                  />
                  <p className="text-xs text-muted-foreground">
                    A verification email will be sent to confirm this address.
                  </p>
                </div>
              )}

              {createMutation.error && (
                <p className="text-sm text-destructive">
                  {(createMutation.error as Error).message ||
                    "Failed to create account"}
                </p>
              )}
            </div>

            <ResponsiveDialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={createMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  !name.trim() || !slug.trim() || createMutation.isPending
                }
              >
                {createMutation.isPending ? "Creating..." : "Create Account"}
              </Button>
            </ResponsiveDialogFooter>
          </form>
        )}
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
